import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	ErrorModal,
	HelmetDexteel,
	useContextMenuMESF,
} from "@dexteel/mesf-core";
import { Tree } from "@minoru/react-dnd-treeview";
import {
	DragLayerMonitorProps,
	DropOptions,
	NodeModel,
} from "@minoru/react-dnd-treeview/dist/types";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import {
	MouseEvent as ReactMouseEvent,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useConfigurationDelayCodeContext } from "../../context/ConfigurationDelayCodeContext";
import { useDelayActions } from "../../hooks/delays/useDelayActions";
import { DelayCodeSearchResult } from "../../hooks/delays/useDelayCodeSearch";
import { useSearchDelays } from "../../hooks/delays/useSearchDelays";
import { useDelayCodeOptionsFunctions } from "../../hooks/useDelayCodeOptionsFunctions";
import { CatalogDelayCode } from "../../models/CatalogDelayCode";
import { FileProperties } from "../../models/FileProperties";
import { TreeParameters } from "../../models/TreeParameter";
import { moveDelayCode } from "../../repositories/ConfigurationDelayRepository";
import { CustomNode } from "../CustomNode";
import { DelayCodeSearchInput } from "../DelayCodeSearchInput";
import { CustomDragPreview } from "../DragPreview/CustomDragPreview";
import { MultipleDragPreview } from "../DragPreview/MultipleDragPreview";
import { UpsertDeleteViewDelay } from "../modals/UpsertDeleteViewDelay";

interface TreeViewDelay {
	delayAreaAssetId?: number;
	delayAreaAssetName?: string;
}
export const TreeViewDelay = ({
	delayAreaAssetId,
	delayAreaAssetName,
}: TreeViewDelay) => {
	const [selectedNodes, setSelectedNodes] = useState<
		NodeModel<FileProperties>[]
	>([]);
	const [showModal, setShowModal] = useState<
		"new" | "edit" | "delete" | "view" | ""
	>("");

	const [isDragging, setIsDragging] = useState(false);
	const [isCtrlPressing, setIsCtrlPressing] = useState(false);

	const [delayCodeId, setDelayCodeId] = useState<number | null>();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [updatedSuccessfullyMessage, setUpdatedSuccessfullyMessage] =
		useState("");

	const {
		state: { allDelayNodes, allDelayCodes, openDelays },
		actions: { setDelayNodeSelectedInTree, setDelayAreaAsset, setOpenDelays },
	} = useConfigurationDelayCodeContext();

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { searchDelays } = useSearchDelays({
		setSearchError: setError,
		setAllDelayLoading: setIsLoading,
	});

	const sortBySortOrder = (
		a: NodeModel<FileProperties>,
		b: NodeModel<FileProperties>,
	): number => {
		const nodeA = a as unknown as TreeParameters;
		const nodeB = b as unknown as TreeParameters;

		const sortA = nodeA.sortOrder;
		const sortB = nodeB.sortOrder;

		if (sortA === undefined || sortA === null) {
			if (sortB === undefined || sortB === null) {
				return 0;
			}
			return -1;
		}

		if (sortB === undefined || sortB === null) {
			return 1;
		}

		return sortA - sortB;
	};

	const { handleDrop, handleCtrlClick, handleDragStart, handleDragEnd } =
		useDelayActions({
			delay: allDelayNodes,
			selectedNodes,
			setSelectedNodes,
			setIsDragging,
			isCtrlPressing,
			setIsCtrlPressing,
			onDelayUpdateStart: () => {
				setIsLoading(true);
			},
			onDelayUpdateEnd: (result) => {
				if (result.ok) {
					setUpdatedSuccessfullyMessage("The delay was upsert succesfully");
				} else {
					setError(result.message);
				}
				searchDelays(delayAreaAssetId);
			},
		});

	const handleMoveUp = async ({ delayCodeId }: { delayCodeId: number }) => {
		if (!delayCodeId) return;
		try {
			const result = await moveDelayCode(delayCodeId!, false);
			if (result.ok) {
				setUpdatedSuccessfullyMessage("The delay was moved up succesfully");
			} else {
				setError(result.message);
			}
			await searchDelays(delayAreaAssetId);
		} catch (err) {
			setError("An error occurred while moving the delay code.");
		}
	};

	const handleMoveDown = async ({ delayCodeId }: { delayCodeId: number }) => {
		if (!delayCodeId) return;
		try {
			const result = await moveDelayCode(delayCodeId, true);
			if (result.ok) {
				setUpdatedSuccessfullyMessage("The delay was moved down succesfully");
			} else {
				setError(result.message);
			}
			await searchDelays(delayAreaAssetId);
		} catch (err) {
			setError("An error occurred while moving the delay code.");
		}
	};

	const { getMenuOptions } = useDelayCodeOptionsFunctions({
		setDelayNodeSelectedInTree,
		setDelayCodeId,
		setShowModal,
		handleMoveUp,
		handleMoveDown,
	});

	const handleContextMenu = (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		if (e.type === "contextmenu") {
			setSelectedNodes([data]);
			showContextMenu(e, data, "delayCodeContext");
			const selectedNode = allDelayCodes.find(
				(node: CatalogDelayCode) => node.DelayCodeId === data.id,
			);
			if (selectedNode) {
				setDelayNodeSelectedInTree(selectedNode);
			}
		}
	};

	const handleSearchResultClick = (result: DelayCodeSearchResult) => {
		const nodeMap = new Map<number, NodeModel<FileProperties>>();
		allDelayNodes.forEach((node) => {
			nodeMap.set(node.id as number, node);
		});

		const parentsToExpand: { [key: number | string]: number | string } = {
			...openDelays,
		};
		let currentNode = nodeMap.get(result.id);

		while (currentNode && currentNode.parent !== 0) {
			const parentId = currentNode.parent as number;
			parentsToExpand[parentId] = parentId;
			currentNode = nodeMap.get(parentId);
		}

		setOpenDelays(parentsToExpand);

		const nodeToSelect = nodeMap.get(result.id);
		if (nodeToSelect) {
			setSelectedNodes([nodeToSelect]);

			const selectedDelayCode = allDelayCodes.find(
				(code: CatalogDelayCode) => code.DelayCodeId === result.id,
			);
			if (selectedDelayCode) {
				setDelayNodeSelectedInTree(selectedDelayCode);
			}

			setTimeout(() => {
				const nodeElement = document.querySelector(
					`[data-node-id="${result.id}"]`,
				);
				if (nodeElement) {
					nodeElement.scrollIntoView({
						behavior: "smooth",
						block: "center",
					});
				}
			}, 100);
		}
	};

	const onModalHide = (shouldUpdate: boolean) => {
		if (shouldUpdate) searchDelays(delayAreaAssetId);
		setShowModal("");
	};

	const openDelaysArray = useMemo(() => {
		return Object.keys(openDelays).map((el) => parseInt(el));
	}, [openDelays]);

	useEffect(() => {
		registerConfig({
			id: "delayCodeContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		const delayAreaAsset = {
			AssetId: delayAreaAssetId,
			AssetName: delayAreaAssetName,
		};
		setDelayAreaAsset(delayAreaAsset);
		searchDelays(delayAreaAssetId);
	}, [delayAreaAssetId]);

	return (
		<>
			<HelmetDexteel title="Delay Codes Tree" />
			<Grid2
				container
				p={1}
				spacing={1}
				onContextMenu={(e) => {
					e.preventDefault();
					showContextMenu(e, null, "delayCodeContext");
				}}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Delay Codes Tree
						{delayAreaAssetName ? ` - ${delayAreaAssetName}` : ""}
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<DelayCodeSearchInput
						delayNodes={allDelayNodes}
						onSearchResultClick={handleSearchResultClick}
					/>
				</Grid2>
				{isLoading ? (
					<CenteredLazyLoading />
				) : (
					<Paper
						elevation={2}
						sx={{
							width: "100%",
							borderRadius: "0.75rem",
							padding: 2,
						}}
					>
						<Grid2 container alignItems="center">
							<Box
								sx={{
									height: "60vh",
									width: "100%",
									overflowY: "auto",
									paddingBottom: 10,
									"& .root": {
										boxSizing: "border-box",
										height: "100%",
										padding: 2,
									},
									"& .dropTarget": {
										boxShadow: "0 0 0 1px #1967d2 inset",
									},
								}}
							>
								<Tree
									key={`${JSON.stringify(allDelayNodes)}-${openDelaysArray.join(",")}`}
									rootId={0}
									initialOpen={openDelaysArray}
									tree={allDelayNodes}
									sort={sortBySortOrder}
									insertDroppableFirst={false}
									enableAnimateExpand={true}
									classes={{
										root: "root",
										dropTarget: "dropTarget",
									}}
									onDrop={handleDrop}
									onDragStart={handleDragStart}
									onDragEnd={handleDragEnd}
									canDrop={(
										dcode: NodeModel<FileProperties>[],
										options: DropOptions<FileProperties>,
									): boolean => {
										// If a code is being drop on an undeclared code, cancel the operation
										if (!(options?.dropTarget as TreeParameters)?.isEditable) {
											return false;
										}

										// If an undeclared code is being drop, cancel the operation
										if (
											selectedNodes.some(
												(selectedNode) =>
													!(selectedNode as TreeParameters)?.isEditable,
											)
										) {
											return false;
										}

										return !selectedNodes.some(
											(selectedNode) =>
												selectedNode.id === options.dropTargetId,
										);
									}}
									render={(node, options) => {
										const selected = selectedNodes.some(
											(selectedNode) => selectedNode.id === node.id,
										);
										return (
											<CustomNode
												setContextMenuOver={handleContextMenu}
												node={node}
												{...options}
												isSelected={selected}
												isDragging={selected && isDragging}
												onClick={handleCtrlClick}
												onDoubleClick={() => {}}
											/>
										);
									}}
									dragPreviewRender={(
										monitorProps: DragLayerMonitorProps<FileProperties>,
									) => {
										if (selectedNodes.length > 1) {
											return (
												<MultipleDragPreview dragSources={selectedNodes} />
											);
										}
										return <CustomDragPreview monitorProps={monitorProps} />;
									}}
								/>
							</Box>
						</Grid2>
					</Paper>
				)}
				<FooterButton
					label="DELAY CODE"
					showCreateModal={showModal === "new"}
					setShowCreateModal={(showCreateModal) => {
						setDelayNodeSelectedInTree(null);
						showCreateModal ? setShowModal("new") : setShowModal("");
					}}
				/>
			</Grid2>
			<UpsertDeleteViewDelay
				DelayId={delayCodeId}
				modal={showModal}
				onHide={onModalHide}
				assetId={delayAreaAssetId || null}
			/>
			<NotificationSnackBar
				message={updatedSuccessfullyMessage}
				onHide={() => setUpdatedSuccessfullyMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
